import type { ApolloError } from '@apollo/client';
import { useMachine } from '@xstate/react';
import React, { createContext, useContext, useEffect } from 'react';

import { DesignConfigCategories } from 'src/modules/designStudio/constants/designConfig';
import type { MenuItemType } from 'src/modules/designStudio/constants/hardCodedData/textureData';
import { usePartner } from 'src/modules/designStudio/hooks';
import type {
  Geometry,
  Metadata,
} from 'src/modules/designStudio/hooks/useFetchModelData/types';
import type {
  FilterTracker,
  FilteredColorsData,
  FilteredTexturesData,
  Textures,
} from 'src/modules/designStudio/machines/productCatalog';
import { createMachine } from 'src/modules/designStudio/machines/productCatalog';
import { context as ModeContext } from 'src/modules/designStudio/providers/ModeProvider';
import { useSplit, useTreatment } from 'src/shared/hooks';

const defaultContext = {
  textures: null,
  menuItems: [],
  filteredColors: {
    [DesignConfigCategories.ROOF]: [],
    [DesignConfigCategories.WALL]: [],
    [DesignConfigCategories.TRIM]: [],
    [DesignConfigCategories.WINDOW]: [],
  },
  filteredTextures: {},
  filterTracker: {},
  geometry: null,
  metadata: null,
};

export type MenuItemsType = MenuItemType[];

const context = createContext<{
  textures: Textures;
  menuItems: MenuItemsType;
  filteredColors: FilteredColorsData;
  filteredTextures: FilteredTexturesData;
  filterTracker: FilterTracker;
  shareToken?: string;
  geometry: Geometry;
  metadata: Metadata;
  authToken?: string;
  shareError?: ApolloError;
}>(defaultContext);

const Provider = ({
  children,
  jobId,
  shareToken,
}: {
  children: React.ReactNode;
  jobId?: number;
  shareToken?: string;
}) => {
  const { mode } = useContext(ModeContext);
  const { client: splitClient } = useSplit();
  const shouldUseJhItems = useTreatment('DS_james-hardie-products') === 'on';
  const partner = usePartner();
  const [state, send] = useMachine(() =>
    createMachine(mode, partner, splitClient, jobId, shareToken),
  );

  useEffect(() => {
    if (
      shouldUseJhItems &&
      state.value === 'idle' &&
      !state.context.hasUpdatedExperiments
    ) {
      send('UPDATE_EXPERIMENTS_EVENT', {
        data: { shouldShowPartnerProducts: shouldUseJhItems },
      });
    }
  }, [
    send,
    shouldUseJhItems,
    state.value,
    state.context.hasUpdatedExperiments,
  ]);

  const {
    menuItems,
    textures,
    filteredColors,
    filteredTextures,
    filterTracker,
    geometry,
    metadata,
    authToken,
    shareError,
  } = state.context;

  return (
    <context.Provider
      // See DS-846
      /* eslint-disable-next-line react/jsx-no-constructed-context-values */
      value={{
        textures,
        menuItems,
        filteredColors,
        filteredTextures,
        filterTracker,
        geometry,
        metadata,
        authToken,
        shareError,
      }}
    >
      {children}
    </context.Provider>
  );
};

export { Provider, context, defaultContext };
